import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { topSlides, bottomSlides, type HeroSlide } from "@/mocks/heroSlides";

interface SlideProps {
  slide: HeroSlide;
  isActive: boolean;
  registerRef: (el: HTMLDivElement | null, id: string) => void;
  onHoverChange: (id: string, hover: boolean) => void;
}

function Slide({ slide, isActive, registerRef, onHoverChange }: SlideProps) {
  return (
    <div
      ref={(el) => registerRef(el, slide.id)}
      className={`slider__slide absolute inset-0 cursor-pointer overflow-hidden ${
        isActive ? "active opacity-100" : "opacity-0 invisible"
      }`}
      onMouseEnter={() => onHoverChange(slide.id, true)}
      onMouseLeave={() => onHoverChange(slide.id, false)}
    >
      {/* Image */}
      <div
        className="slider__slide__img absolute inset-0 will-change-transform"
        style={{ clipPath: "inset(0%)" }}
      >
        <img
          src={slide.image}
          alt={slide.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/30 via-foreground-950/10 to-foreground-950/40" />
      </div>

      {/* "View more" left/right text */}
      <div className="absolute inset-0 flex items-center justify-between px-8 md:px-16 pointer-events-none z-20">
        <span
          className="slider__slide__more text-[0.7rem] tracking-[0.4em] uppercase text-background-50 font-medium"
          style={{ transform: "translateX(0)" }}
        >
          View
        </span>
        <span
          className="slider__slide__more text-[0.7rem] tracking-[0.4em] uppercase text-background-50 font-medium"
          style={{ transform: "translateX(0)" }}
        >
          More
        </span>
      </div>

    </div>
  );
}

export default function HeroSlider() {
  const [topIndex, setTopIndex] = useState(0);
  const [bottomIndex, setBottomIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const slideRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const timelinesRef = useRef<Record<string, gsap.core.Timeline>>({});
  const navTlRef = useRef<gsap.core.Timeline | null>(null);
  const topIdxRef = useRef(0);
  const botIdxRef = useRef(0);
  const wheelLockRef = useRef(false);

  const registerRef = (el: HTMLDivElement | null, id: string) => {
    slideRefs.current[id] = el;
  };

  // Build hover timelines for each slide
  useEffect(() => {
    const allSlides = [...topSlides, ...bottomSlides];

    allSlides.forEach((slide) => {
      const slideEl = slideRefs.current[slide.id];
      if (!slideEl) return;
      if (timelinesRef.current[slide.id]) return;

      const viewMore = slideEl.querySelectorAll(".slider__slide__more");
      const img = slideEl.querySelector(".slider__slide__img");

      const tl = gsap.timeline({
        paused: true,
        defaults: { duration: 1.1, ease: "power2.inOut" },
      });

      tl.to(img, { clipPath: "inset(18%)", scale: 0.95 }, 0)
        .to(viewMore[0], { x: "2rem" }, 0)
        .to(viewMore[1], { x: "-2rem" }, 0);

      timelinesRef.current[slide.id] = tl;
    });
  }, [topIndex, bottomIndex]);

  const handleHover = (id: string, hover: boolean) => {
    const tl = timelinesRef.current[id];
    if (!tl) return;
    if (hover) tl.play();
    else tl.reverse();
  };

  const handleNav = (direction: "up" | "down"): boolean => {
    if (navTlRef.current?.isActive()) return false;

    // Clamp: stop at first / last slide (no looping)
    const maxTop = topSlides.length - 1;
    const maxBot = bottomSlides.length - 1;
    if (direction === "down") {
      if (topIdxRef.current >= maxTop && botIdxRef.current >= maxBot) {
        return false;
      }
    } else {
      if (topIdxRef.current <= 0 && botIdxRef.current <= 0) {
        return false;
      }
    }

    const navTl = gsap.timeline({ defaults: { duration: 1 } });
    navTlRef.current = navTl;

    [0, 1].forEach((k) => {
      const isTop = k === 0;
      const slides = isTop ? topSlides : bottomSlides;
      const currentIdx = isTop ? topIdxRef.current : botIdxRef.current;
      const lastIdx = slides.length - 1;
      const nextIdx =
        direction === "down"
          ? Math.min(currentIdx + 1, lastIdx)
          : Math.max(currentIdx - 1, 0);

      // Nothing to do for this side
      if (nextIdx === currentIdx) return;

      let initialClip: string;
      if (direction === "up") {
        initialClip = k === 0 ? "inset(100% 0 0%)" : "inset(0 0 100%)";
      } else {
        initialClip = k === 1 ? "inset(100% 0 0%)" : "inset(0 0 100%)";
      }

      const activeSlide = slideRefs.current[slides[currentIdx].id];
      const nextSlide = slideRefs.current[slides[nextIdx].id];
      if (!activeSlide || !nextSlide) return;

      gsap.set(nextSlide, {
        autoAlpha: 1,
        scale: 1.2,
        clipPath: initialClip,
      });

      navTl.to(
        nextSlide,
        {
          clipPath: "inset(0% 0 0%)",
          scale: 1,
          ease: "power2.inOut",
          duration: 1,
          onComplete: () => {
            gsap.set(activeSlide, { autoAlpha: 0 });
          },
        },
        0
      );

      if (isTop) {
        topIdxRef.current = nextIdx;
        setTopIndex(nextIdx);
      } else {
        botIdxRef.current = nextIdx;
        setBottomIndex(nextIdx);
      }
    });
  };

  // Wheel / touch scroll triggers navigation
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (wheelLockRef.current) return;
      if (Math.abs(e.deltaY) < 10) return;
      const moved = handleNav(e.deltaY > 0 ? "down" : "up");
      if (!moved) return;
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 1200);
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const endY = e.changedTouches[0]?.clientY ?? 0;
      const diff = touchStartY - endY;
      if (Math.abs(diff) < 40) return;
      if (wheelLockRef.current) return;
      const moved = handleNav(diff > 0 ? "down" : "up");
      if (!moved) return;
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 1200);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (wheelLockRef.current) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const moved = handleNav(e.key === "ArrowDown" ? "down" : "up");
      if (!moved) return;
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 1200);
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("keydown", handleKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-background-950 text-background-50 select-none">
      {/* Top nav */}
      <header className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-6">
        <a href="#" className="flex items-center gap-2 text-background-50">
          <span
            className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center text-foreground-950 font-heading text-sm"
          >
            E
          </span>
          <span className="font-heading text-lg tracking-widest">ECHOES</span>
        </a>
        <nav className="hidden md:flex items-center gap-10 text-[0.7rem] tracking-[0.35em] uppercase text-background-50/80">
          <a href="#" className="hover:text-background-50 transition-colors">
            Journeys
          </a>
          <a href="#" className="hover:text-background-50 transition-colors">
            Field Notes
          </a>
          <a href="#" className="hover:text-background-50 transition-colors">
            Atlas
          </a>
          <a href="#" className="hover:text-background-50 transition-colors">
            Contact
          </a>
        </nav>
        <button
          onClick={() => setModalOpen(true)}
          className="js-trigger whitespace-nowrap rounded-full border border-background-50/40 hover:border-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors px-5 py-2 text-[0.65rem] tracking-[0.35em] uppercase cursor-pointer"
        >
          Book a trip
        </button>
      </header>

      {/* Slider container - split */}
      <div className="absolute inset-0 flex flex-col">
        {/* Top side */}
        <div className="slider__side relative w-full h-1/2 overflow-hidden">
          {topSlides.map((slide, i) => (
            <Slide
              key={slide.id}
              slide={slide}
              isActive={i === topIndex}
              registerRef={registerRef}
              onHoverChange={handleHover}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="relative h-px w-full bg-background-50/10 z-30" />

        {/* Bottom side */}
        <div className="slider__side relative w-full h-1/2 overflow-hidden">
          {bottomSlides.map((slide, i) => (
            <Slide
              key={slide.id}
              slide={slide}
              isActive={i === bottomIndex}
              registerRef={registerRef}
              onHoverChange={handleHover}
            />
          ))}
        </div>
      </div>

      {/* Center hero title + counter — split exactly along the middle divider */}
      <div className="absolute inset-0 z-40 pointer-events-none flex flex-col items-center justify-center text-background-50">
        {/* Top half: "Echoes" sitting on the divider */}
        <div className="flex-1 w-full flex flex-col items-center justify-end pb-2">
          <h1 className="font-heading font-light text-center leading-[0.85] tracking-tight text-[5rem] md:text-[8rem] lg:text-[10rem]">
            <span className="block italic">Echoes</span>
          </h1>
        </div>
        {/* Bottom half: "of the Wild" sitting on the divider + label + counter */}
        <div className="flex-1 w-full flex flex-col items-center justify-start pt-2">
          <h1 className="font-heading font-light text-center leading-[0.85] tracking-tight text-[5rem] md:text-[8rem] lg:text-[10rem] text-background-50/85">
            <span className="block">of the Wild</span>
          </h1>
        </div>
      </div>

      {/* Bottom caption */}
      <div className="absolute bottom-6 left-6 md:left-12 right-6 md:right-12 z-40 flex items-end justify-between text-[0.65rem] tracking-[0.35em] uppercase text-background-50/70">
        <p className="max-w-xs leading-relaxed">
          A field journal of slow journeys, far horizons and quiet rooms.
        </p>
        <p>© Echoes Studio · 2026</p>
      </div>

      {/* Modal */}
      <div
        onClick={() => setModalOpen(false)}
        className={`modal fixed inset-0 z-[60] bg-foreground-950/80 backdrop-blur-sm flex items-center justify-center transition-all duration-500 ${
          modalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-background-50 text-foreground-950 rounded-lg w-full max-w-md mx-4 p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-2xl">Plan your trip</h3>
            <button
              onClick={() => setModalOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <p className="text-sm text-foreground-700 leading-relaxed mb-6">
            Tell us a little about where you would like to wander next and our
            field team will reach out within 48 hours with a curated route.
          </p>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Your name"
              className="w-full rounded-md border border-background-200 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full rounded-md border border-background-200 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
            />
            <button className="w-full rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 py-3 text-xs tracking-[0.3em] uppercase hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Send Request
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}